import { useEffect, useState } from "react";
import { Ban, Loader2, RefreshCw, User, Shield, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../services/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function durationBetween(startIso, endIso) {
  if (!startIso || !endIso) return "—";
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "—";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function AdminCancellations() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/cancellations?limit=200");
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load cancellations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="space-y-6 max-w-7xl" data-testid="admin-cancellations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
            <Ban className="h-5 w-5 text-rose-600" />
          </div>
          <div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900">Cancellations</h1>
            <p className="text-xs text-slate-500">Audit log of every cancelled order — who cancelled, when, and why.</p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={fetchData}
          className="rounded-xl"
          data-testid="cancellations-refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border border-slate-100 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total Cancelled</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="total-cancelled">{total}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">By Customer</p>
          <p className="text-2xl font-bold text-rose-600 mt-1">{items.filter((i) => i.cancelled_by === "customer").length}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">By Admin</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{items.filter((i) => i.cancelled_by === "admin").length}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400" data-testid="cancellations-empty">
            No cancelled orders yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="cancellations-table">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="pb-2 font-semibold">ORDER</th>
                  <th className="pb-2 font-semibold">CANCELLED BY</th>
                  <th className="pb-2 font-semibold">REASON</th>
                  <th className="pb-2 font-semibold">ACCEPTED BY</th>
                  <th className="pb-2 font-semibold">ACCEPTED AT</th>
                  <th className="pb-2 font-semibold">CANCELLED AT</th>
                  <th className="pb-2 font-semibold">TIME-TO-CANCEL</th>
                  <th className="pb-2 font-semibold text-right">AMT</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.tracking_id} className="border-b border-slate-50 last:border-0 align-top" data-testid={`cancellation-row-${i.tracking_id}`}>
                    <td className="py-3">
                      <div className="font-mono font-bold text-slate-900">{i.tracking_id}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{i.user_email || "—"}</div>
                      {i.previous_status && (
                        <div className="text-[10px] text-slate-400">was: {i.previous_status}</div>
                      )}
                    </td>
                    <td className="py-3">
                      {i.cancelled_by === "customer" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-100">
                          <User className="h-2.5 w-2.5" /> Customer
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-100">
                          <Shield className="h-2.5 w-2.5" /> Admin
                        </span>
                      )}
                      {i.cancelled_by_email && (
                        <div className="text-[10px] text-slate-400 mt-0.5">{i.cancelled_by_email}</div>
                      )}
                    </td>
                    <td className="py-3 text-slate-700 max-w-xs">{i.cancellation_reason || "—"}</td>
                    <td className="py-3 text-slate-700">
                      {i.accepted_by_role ? (
                        <span className="capitalize">{i.accepted_by_role}{i.accepted_by_name ? ` — ${i.accepted_by_name}` : ""}</span>
                      ) : (
                        <span className="text-slate-400">not accepted</span>
                      )}
                    </td>
                    <td className="py-3 text-slate-600">{fmtDate(i.accepted_at)}</td>
                    <td className="py-3 text-slate-600">{fmtDate(i.cancelled_at)}</td>
                    <td className="py-3 text-slate-700">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3 text-slate-400" /> {durationBetween(i.accepted_at, i.cancelled_at)}
                      </span>
                    </td>
                    <td className="py-3 text-right font-semibold text-slate-900">{i.amount ? `INR ${i.amount}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
